/**
 * describeTurns(history): what happened in each turn, in plain data, for TurnTimeline. Pure.
 *
 * history = useDiagHistory entries [{seq, turnNo, answers, diag}], oldest first. Each turn is read against the one before it:
 * the facts first observed in it, the causes it ruled out, the leading cause with its percentage and the move since the previous
 * turn (or that the lead changed hands).
 * Returns [{seq, n, total, live, ended, answers, facts: [{label, value}], lead: {key, label, pct, delta, changed} | null, ruledOut: [label]}].
 */
import { CAUSE_STATUS } from "./lightsConstants";

const list = (v) => (Array.isArray(v) ? v : []);
const CLOSED = new Set([CAUSE_STATUS.RULED_OUT, CAUSE_STATUS.FAILED]);
const AHEAD = new Set([CAUSE_STATUS.LEADING, CAUSE_STATUS.CONFIRMED]);
const ENDED = "ended";

/** The cause in front: a leading / confirmed one, else the open cause with the highest percentage. */
export function leadOf(diag) {
  const open = list(diag?.causes).filter((c) => c && c.key && !CLOSED.has(c.status));
  if (!open.length) return null;
  const c = open.find((x) => AHEAD.has(x.status)) || open.reduce((a, b) => ((Number(b.percentage) || 0) > (Number(a.percentage) || 0) ? b : a));
  return { key: c.key, label: c.name || c.key, pct: Number(c.percentage) || 0 };
}

const nameOf = (diag, key) => list(diag?.causes).find((c) => c.key === key)?.name || key;

export function describeTurns(history) {
  const turns = list(history);
  return turns.map((t, i) => {
    const prev = i > 0 ? turns[i - 1].diag : null;
    const diag = t.diag || {};
    const lead = leadOf(diag);
    let delta = null;
    let changed = false;
    if (lead && prev) {
      const before = list(prev.causes).find((c) => c.key === lead.key);
      delta = before ? Math.round(lead.pct - (Number(before.percentage) || 0)) : null;
      const prevLead = leadOf(prev);
      changed = !!prevLead && prevLead.key !== lead.key;
    }
    const seenBefore = new Set(list(prev?.observed).map((o) => o.id));
    const ruledBefore = new Set(list(prev?.ruled_out).map((r) => r.cause_key));
    return {
      seq: t.seq,
      n: i + 1,
      total: turns.length,
      live: i === turns.length - 1,
      ended: diag.lifecycle === ENDED,
      answers: list(t.answers).filter((a) => a && (a.question || a.answer)),
      facts: list(diag.observed).filter((o) => o && o.id && !seenBefore.has(o.id)).map((o) => ({ label: o.label || o.id, value: o.value })),
      lead: lead ? { ...lead, delta, changed } : null,
      ruledOut: list(diag.ruled_out).filter((r) => r && r.cause_key && !ruledBefore.has(r.cause_key)).map((r) => nameOf(diag, r.cause_key)),
    };
  });
}
